/**
 * SZKIC ROZPOCZĘTEGO ZGŁOSZENIA — żeby zamknięcie okna nie kasowało wpisanej treści.
 *
 * [POLECENIE WŁAŚCICIELA 2026-09-12] „optymalizcaje w zakresie **wprowadznai danych** i ogólnej
 * obslugi UX i UI aplikacji”. Ta pozycja została w pierwszej wersji rozliczenia zgubiona.
 *
 * ── CO BYŁO ŹLE ─────────────────────────────────────────────────────────────────────────────
 * Okno „Utwórz nowe zgłoszenie” trzyma 42 stany: temat, opis, pozycje zapotrzebowania, pola własne
 * kafelka, wybór dysków i poziomy dostępu, formularz nowego dysku, dane osobowe wniosku. Zamknięcie
 * okna wołało `resetForm()` i **wszystko znikało bez ostrzeżenia**. Pracownik wypisujący pozycje
 * zapotrzebowania tracił kilkanaście minut pracy przez jedno kliknięcie obok.
 *
 * ── DLACZEGO `sessionStorage`, A NIGDY `localStorage` ───────────────────────────────────────
 * 🔴 To jest decyzja bezpieczeństwa, nie wygody. `localStorage` **przeżywa zamknięcie przeglądarki
 * i wylogowanie**, więc na wspólnym stanowisku (produkcja, magazyn, portiernia) następna osoba
 * zobaczyłaby szkic poprzednika — razem z treścią, nazwiskami i numerami seryjnymi. Ta sama reguła
 * stoi w pamięci powłoki (`hooks/usePamiecPowloki.js`). `sessionStorage` żyje **wyłącznie w tej
 * jednej karcie i ginie razem z nią**, a od wylogowania chroni go warstwa niżej (punkty 1–2).
 *
 * [polecenie właściciela 2026-09-20] „Utworz nowe zgloszenie — zrob tak, ze odswiezene strony nie
 * zamknie tego okna.” Do 20.09 szkic leżał WYŁĄCZNIE w module JS, czyli w pamięci karty: F5 kasował
 * go w całości razem z całym stanem React. Samo zapamiętanie „okno było otwarte” bez treści byłoby
 * gorsze niż nic — okno wracałoby PUSTE i człowiek dopiero wtedy wiedziałby, że stracił pracę.
 * Dlatego do `sessionStorage` idzie JEDNO I DRUGIE: znacznik otwarcia (z krokiem i wybranym oknem)
 * oraz sam szkic. Mapa w module zostaje jako szybka ścieżka w obrębie jednego wczytania strony.
 *
 * ── CZTERY ZABEZPIECZENIA ───────────────────────────────────────────────────────────────────
 *  1. **Szkic nosi tożsamość.** Odczyt z innym identyfikatorem to PUDŁO — po przelogowaniu na tym
 *     samym stanowisku nikt nie dostanie szkicu poprzednika, nawet bez przeładowania strony.
 *     Dla mapy tożsamość jest KLUCZEM, dla `sessionStorage` — polem `tozsamosc` porównywanym przy
 *     każdym odczycie. Tam to porównanie NIE jest ozdobą: wpis przeżywa przeładowanie strony, więc
 *     bez niego wystarczyłoby wylogować się, zalogować na cudze konto i odświeżyć.
 *  2. **Wylogowanie i zmiana uprawnień kasują wszystko** (te same zdarzenia, co w pamięci powłoki)
 *     — razem z wpisem w `sessionStorage`, nie tylko z mapą.
 *  3. **Pusty szkic nie jest szkicem.** Samo otwarcie i zamknięcie okna nie ma czego przywracać,
 *     więc nie pokazujemy paska „przywrócono” po kliknięciu w nic — to by tylko myliło.
 *  4. **Świadome zamknięcie kasuje znacznik otwarcia.** Okno przywrócone po F5 da się zamknąć
 *     i po kolejnym F5 NIE wraca — inaczej byłoby nie do pozbycia się.
 *
 * ⚠ KAŻDY dostęp do `sessionStorage` jest w `try/catch`. W trybie prywatnym, przy zablokowanych
 * danych witryny i w podglądach sam odczyt potrafi rzucić wyjątkiem — a okno zakładania zgłoszenia
 * ma wtedy działać jak dotąd (pamięć karty), a nie wywalić się na starcie.
 *
 * ⚠ ZAŁĄCZNIKÓW NIE DA SIĘ ODTWORZYĆ. Obiekty plików wybranych w okienku systemowym nie przeżywają
 * zamknięcia okna i nie ma sposobu, żeby je przywrócić bez ponownego wskazania. Pasek przywrócenia
 * mówi o tym WPROST, zamiast milczeć i pozwolić człowiekowi wysłać zgłoszenie bez skanu.
 */

/** Pola, które przywracamy. Reszta stanów okna to dane z serwera albo stan przejściowy interfejsu. */
export const POLA_SZKICU = [
    "step", "selectedType", "selectedTileCategory", "tileSubject", "tileBody", "tileItems",
    "tileSubcategoryId", "tileCustomFields", "sourceTileCategory",
    "categoryId", "priority", "dynamicFields", "dodatkoweOsoby",
    "selectedDrives", "driveAccessLevels", "chceNowyDysk", "nowyDysk",
    /* [BH 2026-09-19] Zakres wniosku o zasoby. Lista jest BIAŁĄ LISTĄ — pole, którego tu nie ma,
       jest po cichu gubione przy zapisie szkicu. Wybór spółki i lista osób „dla kogo" to
       kilkanaście kliknięć; przywrócony szkic bez nich wysyłałby wniosek o co innego. */
    "spolkaWniosku", "rodzajZasobu", "osobyDostepu", "nowaWitryna",
    "buyoutData", "selectedBuyoutDevice", "serialConfirmed", "correctedSerials",
    "sapData", "requestItems", "personalData",
];

const szkice = new Map();

/* ═══════════════════════════════════════════════════════════════════════════════════════════
   PRZETRWANIE ODŚWIEŻENIA STRONY (`sessionStorage`)
   ═══════════════════════════════════════════════════════════════════════════════════════════
   Jeden wpis na kartę: w karcie jest zalogowane JEDNO konto, więc drugi slot byłby miejscem,
   w którym może leżeć szkic kogoś innego. Kształt wpisu:
       { tozsamosc: "7", otwarte: true, nawigacja: { step, selectedType, … }, szkic: { … } }
   `otwarte` i `szkic` są NIEZALEŻNE: okno bez wpisanej treści też ma wrócić po F5 (wtedy wraca
   na swój krok, bez paska „przywrócono”, bo nie ma czego przywracać). */
const KLUCZ_SESJI = "dogsoft:szkic-zgloszenia";

/** Tożsamość w jednym kształcie dla mapy i dla `sessionStorage` — inaczej jedno by nie trafiało w drugie. */
const tozsamosc = (userId) => (userId === null || userId === undefined ? null : String(userId));

const magazyn = () => {
    try {
        return typeof window !== "undefined" ? window.sessionStorage : null;
    } catch {
        return null;   // dane witryny zablokowane — sam dostęp do właściwości rzuca
    }
};

/** Wpis z pamięci karty, ale TYLKO gdy należy do pytającego konta. Inaczej `null`. */
const wpisSesji = (userId) => {
    const kto = tozsamosc(userId);
    if (kto === null) return null;
    const m = magazyn();
    if (!m) return null;
    try {
        const surowy = m.getItem(KLUCZ_SESJI);
        if (!surowy) return null;
        const wpis = JSON.parse(surowy);
        /* 🔴 TO PORÓWNANIE JEST CAŁĄ OCHRONĄ TEGO WPISU. W mapie tożsamość jest kluczem, więc
           cudzego szkicu nie da się nawet zaadresować; tutaj wpis jest jeden i leży pod stałym
           kluczem, a przeżywa przeładowanie strony — bez tej linii wystarczyłoby przelogowanie
           i F5, żeby zobaczyć zgłoszenie poprzednika. */
        if (!wpis || wpis.tozsamosc !== kto) return null;
        return wpis;
    } catch {
        return null;
    }
};

const zapiszWpisSesji = (userId, zmiana) => {
    const kto = tozsamosc(userId);
    if (kto === null) return;
    const m = magazyn();
    if (!m) return;
    try {
        const poprzedni = wpisSesji(userId) || { tozsamosc: kto };
        m.setItem(KLUCZ_SESJI, JSON.stringify({ ...poprzedni, ...zmiana, tozsamosc: kto }));
    } catch {
        /* Brak miejsca albo zablokowane dane witryny — szkic zostaje w pamięci karty.
           Cisza jest tu właściwa: to degradacja wygody, a nie błąd, o którym trzeba krzyczeć. */
    }
};

const usunWpisSesji = () => {
    const m = magazyn();
    if (!m) return;
    try { m.removeItem(KLUCZ_SESJI); } catch { /* jak wyżej */ }
};

/* Zdarzenia, po których szkic przestaje być wiarygodny — te same, co w pamięci powłoki.
   ⚠ Kasujemy OBIE warstwy. Gdyby czyszczenie objęło samą mapę, wylogowanie wyglądałoby na
   skuteczne aż do pierwszego odświeżenia strony — a wtedy szkic poprzednika wróciłby z dysku. */
["sesja-zakonczona", "uprawnienia-zmienione"].forEach((zdarzenie) => {
    if (typeof window !== "undefined") {
        window.addEventListener(zdarzenie, () => { szkice.clear(); usunWpisSesji(); });
    }
});

/**
 * Czy w tym szkicu cokolwiek jest. Porównujemy z wartościami PUSTYMI, a nie z „czymkolwiek”:
 * samo wejście w kafelek ustawia `selectedType` i `step`, a to jeszcze nie jest praca do ratowania.
 */
export const szkicMaTresc = (stan) => {
    if (!stan) return false;

    const niepustyTekst = (w) => typeof w === "string" && w.trim().length > 0;
    const niepustyObiekt = (o) => o && typeof o === "object"
        && Object.values(o).some((w) => (Array.isArray(w) ? w.length > 0 : niepustyTekst(String(w ?? ""))));

    if (niepustyTekst(stan.tileSubject) || niepustyTekst(stan.tileBody)) return true;
    if (niepustyTekst(stan.categoryId)) return true;
    if ((stan.selectedDrives || []).length > 0) return true;
    if (stan.chceNowyDysk) return true;
    if ((stan.dodatkoweOsoby || []).length > 0) return true;
    if (niepustyObiekt(stan.dynamicFields) || niepustyObiekt(stan.tileCustomFields)) return true;
    if (niepustyObiekt(stan.sapData) || niepustyObiekt(stan.personalData)) return true;
    if (niepustyObiekt(stan.buyoutData)) return true;

    // Pozycje listowe: pusty wiersz startowy NIE jest treścią.
    const maWypelnionaPozycje = (lista, pola) => (lista || [])
        .some((p) => pola.some((k) => niepustyTekst(String(p?.[k] ?? ""))));
    if (maWypelnionaPozycje(stan.tileItems, ["name", "note"])) return true;
    if (maWypelnionaPozycje(stan.requestItems, ["type", "nameOrSerial", "forWhom"])) return true;

    return false;
};

/** Zapisuje szkic dla KONKRETNEGO konta. Pusty szkic usuwa wpis, zamiast zostawiać śmieć. */
export const zapiszSzkic = (userId, stan) => {
    if (userId == null) return;
    if (!szkicMaTresc(stan)) {
        szkice.delete(String(userId));
        /* Wpis w pamięci karty gubi TYLKO treść. Znacznik otwarcia zostaje: skasowana treść nie
           znaczy „zamknij mi okno”, a człowiek, który wyczyścił formularz i odświeżył stronę,
           ma dostać to samo okno, a nie pulpit. */
        zapiszWpisSesji(userId, { szkic: null });
        return;
    }
    const wybrane = {};
    POLA_SZKICU.forEach((k) => { if (k in stan) wybrane[k] = stan[k]; });
    szkice.set(String(userId), wybrane);
    zapiszWpisSesji(userId, { szkic: wybrane });
};

/**
 * Odczyt szkicu.
 *
 * ⚠ OCHRONA STOI NA KLUCZU, nie na dodatkowym porównaniu pola. Pierwsza wersja trzymała przy szkicu
 * pole `tozsamosc` i porównywała je przy odczycie — sabotaż usuwający to porównanie NIC nie zmieniał,
 * bo wpis i tak jest dostępny wyłącznie pod kluczem swojego konta. Był to więc kod udający drugą
 * bramkę: zero ochrony, a czytający miał poczucie, że coś go pilnuje. Usunięty.
 */
export const odczytajSzkic = (userId) => {
    if (userId == null) return null;
    const zPamieci = szkice.get(String(userId));
    if (zPamieci) return zPamieci;
    /* Po odświeżeniu strony mapa jest pusta (moduł wczytuje się od nowa), a wpis karty żyje.
       `wpisSesji` sam odsiewa cudzą tożsamość, więc ta gałąź nie jest furtką obok bramki. */
    const wpis = wpisSesji(userId);
    if (!wpis || !wpis.szkic) return null;
    szkice.set(String(userId), wpis.szkic);   // następny odczyt idzie już szybką ścieżką
    return wpis.szkic;
};

export const wyczyscSzkic = (userId) => {
    if (userId == null) szkice.clear();
    else szkice.delete(String(userId));
    /* Wpis karty jest JEDEN i należy do zalogowanego konta, więc kasujemy go w obu przypadkach:
       „wyczyść wszystko” i „wyczyść moje”. Zostawienie go po wysłanym zgłoszeniu przywróciłoby
       po F5 sprawę, która już poszła do obsługi. */
    usunWpisSesji();
};

/* ═══════════════════════════════════════════════════════════════════════════════════════════
   ZNACZNIK OTWARCIA OKNA — „odświeżenie strony nie zamyka okna"
   ═══════════════════════════════════════════════════════════════════════════════════════════ */

/** Pola nawigacji, które wystarczają, żeby wrócić NA TEN SAM KROK, także bez wpisanej treści. */
const POLA_NAWIGACJI = ["step", "selectedType", "selectedTileCategory", "sourceTileCategory"];

/**
 * Zapamiętuje, że okno JEST otwarte, i na czym stoi.
 *
 * Wołane przy każdej zmianie kroku/wybranego okna, a nie raz przy otwarciu: F5 nie zapowiada się
 * niczym, co dałoby się złapać z pewnością (`beforeunload` nie wykona się przy zamknięciu karty
 * z awarii ani przy odcięciu zasilania), więc znacznik ma być aktualny CAŁY CZAS.
 */
export const zapamietajOtwarcieOkna = (userId, nawigacja) => {
    if (userId == null) return;
    const wybrane = {};
    POLA_NAWIGACJI.forEach((k) => { if (nawigacja && k in nawigacja) wybrane[k] = nawigacja[k]; });
    zapiszWpisSesji(userId, { otwarte: true, nawigacja: wybrane });
};

/**
 * Kasuje znacznik otwarcia — ŚWIADOME zamknięcie okna (✕ / Anuluj / wysłane zgłoszenie).
 *
 * ⚠ Treść szkicu zostaje: zamknięcie krzyżykiem od 12.09 ratuje pracę, a ta poprawka nie ma
 * tamtego prawa odbierać. Znika sama informacja „okno było otwarte”, żeby okno nie wracało
 * po kolejnym odświeżeniu.
 */
export const zapomnijOtwarcieOkna = (userId) => {
    if (userId == null) return;
    zapiszWpisSesji(userId, { otwarte: false, nawigacja: null });
};

/**
 * Czy przy tym wczytaniu strony należy odtworzyć okno.
 * @returns {{step:any}|null} zapamiętana nawigacja (może być pusta) albo `null`, gdy nie wracamy.
 */
export const odczytajOtwarcieOkna = (userId) => {
    const wpis = wpisSesji(userId);
    if (!wpis || !wpis.otwarte) return null;
    return wpis.nawigacja || {};
};

/**
 * Decyzja „czy zapisać szkic przy zamykaniu okna" — W MODULE, nie w komponencie.
 *
 * <b>Dlaczego tutaj.</b> Pierwsza wersja trzymała ten warunek w ciele okna, a zapadka sprawdzała
 * tylko, czy w źródle WYSTĘPUJE wywołanie `zapiszSzkic(`. Sabotaż zamieniający je na
 * `if (false) zapiszSzkic(...)` przechodził na zielono — martwy kod spełniał warunek. Renderowanie
 * całego okna (42 stany + animacje) okazało się zawieszać w jsdom, więc decyzja stoi tam, gdzie da
 * się ją zmierzyć wprost, a w oknie zostaje jedna linia wpięcia.
 *
 * @returns {boolean} czy szkic został zapisany (do zapadek i do czytania kodu)
 */
export const zapiszPrzyZamknieciu = (userId, wyslano, szkic) => {
    /* [2026-09-20] ŚWIADOME ZAMKNIĘCIE KASUJE ZNACZNIK OTWARCIA — bez wyjątków, także wtedy, gdy
       nie ma czego zapisywać i gdy zgłoszenie właśnie poszło. Inaczej okno przywrócone po F5 nie
       dałoby się zamknąć: zniknęłoby po kliknięciu i wróciło przy następnym odświeżeniu. */
    zapomnijOtwarcieOkna(userId);
    // Zgłoszenie poszło — szkic jest już wyczyszczony i NIE MOŻE wrócić przy następnym otwarciu.
    if (wyslano) return false;
    /* ⚠ Brak konta musi dać `false`. Pierwsza wersja zwracała `true` także bez identyfikatora, bo
       `zapiszSzkic` po cichu nic nie robi — funkcja RAPORTOWAŁA zapis, którego nie było. Własna
       zapadka to złapała; wartość zwracana czyta kod i czytają testy, więc nie może kłamać. */
    if (userId == null) return false;
    if (!szkicMaTresc(szkic)) return false;
    zapiszSzkic(userId, szkic);
    return true;
};

/** Do zapadek: ile szkiców leży w pamięci. */
export const licznikSzkicow = () => szkice.size;
